/**
 * AIActivityPanel — Case-level AI interaction timeline.
 *
 * Shows a chronological feed of every AI agent action taken on a case
 * so humans can see exactly what the AI did and why.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Brain, RefreshCw, ChevronDown, ChevronUp, AlertCircle, Clock, Cpu, Filter } from 'lucide-react';
import { getCaseAIActivity, getCaseAISummary, type AIActivityEntry, type AIActivitySummary } from '../services/aiActivityApi';

interface AIActivityPanelProps {
  reportId: string;
  className?: string;
}

const AGENT_COLORS: Record<string, string> = {
  'Report Narrator': 'bg-purple-100 text-purple-700',
  'Intelligence Agent': 'bg-blue-100 text-blue-700',
  'Forensic Analyzer': 'bg-amber-100 text-amber-700',
  'Settlement Calculator': 'bg-green-100 text-green-700',
  'Future Closure Engine': 'bg-red-100 text-red-700',
  'Edge Case Narrator': 'bg-teal-100 text-teal-700',
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function ActivityEntry({ entry }: { entry: AIActivityEntry }) {
  const [expanded, setExpanded] = useState(false);
  const colorClass = AGENT_COLORS[entry.agent_name] || 'bg-gray-100 text-gray-700';
  const isError = entry.status === 'error';

  return (
    <div className={`border rounded-lg p-3 ${isError ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${colorClass}`}>
            {entry.agent_name}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{entry.action}</p>
            <p className="text-xs text-gray-500 mt-0.5">{formatTimestamp(entry.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {entry.duration_ms > 0 && (
            <span className="text-xs text-gray-400 flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              {formatDuration(entry.duration_ms)}
            </span>
          )}
          {entry.tokens_used > 0 && (
            <span className="text-xs text-gray-400 flex items-center gap-0.5">
              <Cpu className="w-3 h-3" />
              {entry.tokens_used.toLocaleString()}
            </span>
          )}
          {isError && <AlertCircle className="w-4 h-4 text-red-500" />}
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-gray-400 hover:text-gray-600">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2 text-xs border-t border-gray-100 pt-2">
          {entry.input_summary && (
            <div>
              <span className="font-medium text-gray-600">Input:</span>
              <p className="text-gray-500 mt-0.5 whitespace-pre-wrap">{entry.input_summary}</p>
            </div>
          )}
          {entry.output_summary && (
            <div>
              <span className="font-medium text-gray-600">Output:</span>
              <p className="text-gray-500 mt-0.5 whitespace-pre-wrap">{entry.output_summary}</p>
            </div>
          )}
          {isError && entry.error_message && (
            <div>
              <span className="font-medium text-red-600">Error:</span>
              <p className="text-red-500 mt-0.5">{entry.error_message}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 text-gray-400 pt-1">
            {entry.model && <span>Model: {entry.model}</span>}
            {entry.triggered_by && <span>By: {entry.triggered_by}</span>}
            {entry.cost_estimate > 0 && <span>Cost: ${entry.cost_estimate.toFixed(4)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIActivityPanel({ reportId, className = '' }: AIActivityPanelProps) {
  const [entries, setEntries] = useState<AIActivityEntry[]>([]);
  const [summary, setSummary] = useState<AIActivitySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [agentFilter, setAgentFilter] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [activityData, summaryData] = await Promise.all([
        getCaseAIActivity(reportId, { agentName: agentFilter || undefined, limit: 100 }),
        getCaseAISummary(reportId),
      ]);
      setEntries(activityData);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to fetch AI activity:', err);
    } finally {
      setLoading(false);
    }
  }, [reportId, agentFilter]);

  useEffect(() => {
    if (reportId) fetchData();
  }, [reportId, fetchData]);

  const agentNames = summary ? Object.keys(summary.agents) : [];

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Brain className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Activity Trail</h3>
            {summary && summary.total_interactions > 0 && (
              <p className="text-xs text-gray-500">
                {summary.total_interactions} interactions across {agentNames.length} agents
                {summary.total_tokens > 0 && ` \u00B7 ${summary.total_tokens.toLocaleString()} tokens`}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-1.5 text-gray-400 hover:text-violet-600 transition-colors rounded"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Agent filter chips */}
      {agentNames.length > 1 && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-50 overflow-x-auto">
          <Filter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <button
            onClick={() => setAgentFilter('')}
            className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
              !agentFilter ? 'bg-violet-100 text-violet-700 font-medium' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {agentNames.map((name) => (
            <button
              key={name}
              onClick={() => setAgentFilter(name === agentFilter ? '' : name)}
              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                agentFilter === name
                  ? (AGENT_COLORS[name] || 'bg-gray-100 text-gray-700') + ' font-medium'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {name} ({summary?.agents[name]?.count || 0})
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {loading && entries.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 text-violet-400 animate-spin mr-2" />
            <span className="text-sm text-gray-500">Loading AI activity...</span>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8">
            <Brain className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No AI activity recorded for this case yet</p>
            <p className="text-xs text-gray-400 mt-1">Activity will appear here as AI agents analyze this case</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {entries.map((entry) => (
              <ActivityEntry key={entry.activity_id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
